import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { Button } from '@optools/components/ui/button';
import { ButtonGroup } from '@optools/components/ui/button-group';
import { Card } from '@optools/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import { cn } from '@optools/components/ui/utils';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  mediaId: z.string().min(1, 'Please enter a Media ID'),
});

export type ESNIMediaFormValues = z.infer<typeof formSchema>;

export interface ESNIMediaFormProps {
  mediaId: string;
  onProcess: () => void;
  onValidate: (mediaId: string) => void;
  processed: boolean;
  processing: boolean;
  validated: boolean;
  validating: boolean;
}

export function ESNIMediaForm({
  mediaId,
  onProcess,
  onValidate,
  processed,
  processing,
  validated,
  validating,
}: ESNIMediaFormProps) {
  const form = useForm<ESNIMediaFormValues>({
    defaultValues: { mediaId },
    disabled: validating || processing,
    resolver: zodResolver(formSchema),
    reValidateMode: 'onSubmit',
  });

  const mediaIdValue = form.watch('mediaId');

  const handleSubmit = ({ mediaId }: ESNIMediaFormValues) => {
    onValidate(mediaId);
  };

  const processDisabled =
    !validated || mediaIdValue !== mediaId || validating || processing || processed;

  return (
    <Card className="flex items-end gap-2 p-4">
      <Form {...form}>
        <form className="flex-1" onSubmit={form.handleSubmit(handleSubmit)}>
          <FormField
            control={form.control}
            name="mediaId"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>Media ID</FormLabel>
                <ButtonGroup className="w-full">
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <Button
                    disabled={form.formState.disabled}
                    loading={validating}
                    type="submit"
                    variant="outline">
                    Validate
                  </Button>
                </ButtonGroup>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>

      <div className={cn(form.formState.errors['mediaId'] && 'pb-7')}>
        <ConfirmationDialog
          description="Are you sure you want to process this media through the rules?"
          title="Process Media"
          onConfirm={onProcess}>
          <Button disabled={processDisabled} loading={processing}>
            Process
          </Button>
        </ConfirmationDialog>
      </div>
    </Card>
  );
}
